import React, { useState, useEffect } from 'react';
import { trackFunnelStep } from '../analytics';
import { trackMetaViewContent } from '../metaPixel';
import SEOHead from './SEOHead';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Alert,
  CircularProgress,
  Link,
  TextField,
} from '@mui/material';
import axios from 'axios';
import { getAppCheckHeaders } from '../firebase';
import InventoryIcon from '@mui/icons-material/Inventory';

import AssessmentIcon from '@mui/icons-material/Assessment';
import AssistantIcon from '@mui/icons-material/Assistant';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SellIcon from '@mui/icons-material/Sell';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import FacebookIcon from '@mui/icons-material/Facebook';
import LockIcon from '@mui/icons-material/Lock';
import ShieldIcon from '@mui/icons-material/Shield';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import YouTubeIcon from '@mui/icons-material/YouTube';
import PricingCard from './PricingCard';
import ScreenshotCarousel from './ScreenshotCarousel';
import useSubscriptionProducts from '../hooks/useSubscriptionProducts';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  FALLBACK_PLANS,
  AI_TOKEN_ALLOWANCE,
  PRIVACY_LINKS,
  TERMS_CANCELLATION_URL,
} from '../content/marketingContent';


/** Brand gradient background — references theme.palette.brand */
const brandGradientSx = (theme) => ({
  background: `linear-gradient(135deg, ${theme.palette.brand.gradientStart} 0%, ${theme.palette.brand.gradientEnd} 100%)`,
  color: theme.palette.common.white,
});

/** Warm accent call-to-action button used on gradient backgrounds */
const ctaButtonSx = (theme) => ({
  bgcolor: theme.palette.accent.main,
  color: theme.palette.accent.contrastText,
  fontWeight: 700,
  py: 1.5,
  px: 4,
  fontSize: '1.1rem',
  '&:hover': {
    bgcolor: theme.palette.accent.dark,
  },
});

/** Section heading style */
const sectionHeadingSx = {
  fontWeight: 700,
  textAlign: 'center',
  mb: 6,
  color: 'heading.main',
};

/** Feature icon shared style */
const featureIconSx = { fontSize: 40, color: 'common.white' };

const features = [
  {
    icon: <InventoryIcon sx={featureIconSx} />,
    title: 'Easy Item Management',
    description: 'Add and organize inventory items with descriptions, cost, list price, quantity, and images.',
  },
  {
    icon: <SellIcon sx={featureIconSx} />,
    title: 'Multiple Sales Channels',
    description: 'Manage your sales channels: Antique & consignment shops, flea markets, and online platforms.',
  },
    {
    icon: <QrCode2Icon sx={featureIconSx} />,
    title: 'Barcode & Item Number Support',
    description: 'Configure sales channels to auto-generate unique item numbers with multiple formats and prefixes.',
  },
  {
    icon: <ReceiptIcon sx={featureIconSx} />,
    title: 'Track Your Expenses',
    description: 'Track & manage your expenses related to inventory, sales channels, and other costs.',
  },
  {
    icon: <MonetizationOnIcon sx={featureIconSx} />,
    title: 'Track Your Sales',
    description: 'Know your true profit per sale after channel fees and expenses.',
  },
  {
    icon: <AssessmentIcon sx={featureIconSx} />,
    title: 'Analytics & Reports',
    description: "See which items are sell-through stars and which have been sitting too long, with profit measured against each item's cost basis.",
  },
  {
    icon: <PhotoLibraryIcon sx={featureIconSx} />,
    title: 'Image Management',
    description: 'Upload multiple product images per item and set the primary image for each item.',
  },
  {
    icon: <AssistantIcon sx={featureIconSx} />,
    title: 'Reseller Pro AI-Powered Assistant',
    description: 'Get help using the software, add items, get pricing suggestions, and more with our built-in assistant.',
  },
  {
    icon: <DirectionsCarIcon sx={featureIconSx} />,
    title: 'Mileage Tracking',
    description: 'Log every sourcing trip so your tax write-off takes minutes, not a shoebox of notes.',
  },
  {
    icon: <ImportExportIcon sx={featureIconSx} />,
    title: 'Import & Export Data',
    description: 'Import & export Inventory, Expenses, Sales Transactions, and Mileage.',
  },
];

// Plans load from the API; FALLBACK_PLANS cover the case where it can't be reached

const trustPromises = [
  { icon: <ShieldIcon sx={{ color: 'primary.main' }} />, text: 'We never sell your data', href: PRIVACY_LINKS.noSale },
  { icon: <LockIcon sx={{ color: 'primary.main' }} />, text: 'Your data is never used to train AI models', href: PRIVACY_LINKS.noTraining },
  { icon: <CreditCardIcon sx={{ color: 'primary.main' }} />, text: 'Card details handled by Stripe - we never store them', href: PRIVACY_LINKS.cardData },
];

/** Trial terms shown under each sign-up CTA on the gradient sections */
const TrialTerms = () => (
  <Typography variant="body2" sx={{ mt: 2, opacity: 0.9, maxWidth: 560, mx: 'auto' }}>
    7-day free trial.{' '}
    <Link href={TERMS_CANCELLATION_URL} target="_blank" rel="noopener" color="inherit" underline="always">
      Cancel anytime
    </Link>
    {" - you won't be charged until day 8, and we never see or store your card details (payments run through Stripe)."}
  </Typography>
);

const API_BACKEND = process.env.REACT_APP_SHOPKEEP_BACKEND || 'http://localhost:3001';

const SECTION_IDS = {
  pricing: 'pricing-section',
  features: 'features-section',
};

const Landing = ({ onLoginClick, onStartFreeTrial, error, onClearError, pageKey = '/', section }) => {
  const { plans, loadingPlans, error: plansError } = useSubscriptionProducts();
  const navigate = useNavigate();
  const displayPlans = plansError || plans.length === 0 ? FALLBACK_PLANS : plans;

  useEffect(() => {
    trackMetaViewContent();
  }, []);

  // /pricing and /features render this page scrolled to their section
  useEffect(() => {
    const target = section && document.getElementById(SECTION_IDS[section]);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  }, [section]);

  // Newsletter signup state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // 'idle' | 'success' | 'error'
  const [newsletterError, setNewsletterError] = useState('');

  const handleStartFreeTrial = async (priceId) => {
    trackFunnelStep('checkout_start');
    onStartFreeTrial?.(priceId);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNewsletterStatus('error');
      setNewsletterError('Please enter a valid email address.');
      return;
    }
    setNewsletterSubmitting(true);
    setNewsletterStatus('idle');
    setNewsletterError('');
    try {
      const headers = await getAppCheckHeaders();
      const response = await axios.post(`${API_BACKEND}/api/mailing-list/subscribe`, { email }, { headers });
      if (response.data.success) {
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('error');
        setNewsletterError(response.data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setNewsletterStatus('error');
      if (err.response?.status === 429) {
        setNewsletterError('Too many requests. Please try again later.');
      } else if (err.response?.data?.error) {
        setNewsletterError(err.response.data.error);
      } else {
        setNewsletterError('Unable to subscribe at this time. Please try again later.');
      }
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const handleScrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      <SEOHead pageKey={pageKey} />
      {/* Header/Navigation */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          boxShadow: 1,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Icon only on mobile, full text logo on sm+ */}
        <Box
          component="img"
          src="/rp-barcode-icon.svg"
          alt="Reseller Pro"
          sx={{ height: 32, width: 'auto', display: { xs: 'block', md: 'none' } }}
        />
        <Box
          component="img"
          src="/rp-barcode-text-1024x167.svg"
          alt="Reseller Pro"
          sx={{ height: 40, width: 'auto', display: { xs: 'none', md: 'block' } }}
        />
        <Box component="nav" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <Button
            variant="text"
            component={RouterLink}
            to="/pricing"
            sx={{ textTransform: 'none', fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'primary.main' }}
          >
            Pricing
          </Button>
          <Button
            variant="text"
            onClick={() => navigate('/help')}
            sx={{ textTransform: 'none', fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'primary.main' }}
          >
            Help &amp; Support
          </Button>
          <Button
            variant="contained"
            onClick={onLoginClick}
            sx={{ textTransform: 'none', fontSize: '1rem' }}
          >
            Sign In
          </Button>
        </Box>
      </Box>

      {/* Hero Section */}
      <Box
        sx={(theme) => ({
          ...brandGradientSx(theme),
          py: { xs: 8, md: 12 },
          textAlign: 'center',
        })}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            Know Your Real Profit on Every Flip
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              fontSize: { xs: '1rem', md: '1.25rem' },
              fontWeight: 300,
              opacity: 0.95,
            }}
          >
            Reseller Pro replaces the spreadsheet with one system for inventory, expenses, mileage, and every sales channel - antique booths, flea markets, and online - so you always know what you actually made.
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
              onClose={onClearError}
            >
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleScrollToPricing}
            sx={ctaButtonSx}
          >
            Get Started Free
          </Button>
          <TrialTerms />
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features-section" sx={{ py: { xs: 6, md: 10 }, bgcolor: '#e8f0fe' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={sectionHeadingSx}>
            Powerful Features
          </Typography>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ alignItems: 'stretch' }}>
            {features.map((feature, idx) => (
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={idx} sx={{ height: '100%', display: 'flex' }}>
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    boxShadow: 2,
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' },
                  }}
                >
                  <CardContent sx={(theme) => {
                    const colors = theme.palette.featureCards[idx % theme.palette.featureCards.length];
                    return {
                      background: `linear-gradient(135deg, ${colors.start} 0%, ${colors.end} 100%)`,
                      color: theme.palette.common.white,
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                    };
                  }}>
                    <Box sx={{ mb: 2 }}>
                      {feature.icon}
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ flex: 1, opacity: 0.95 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Screenshots Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={sectionHeadingSx}>
            See It in Action
          </Typography>
          <ScreenshotCarousel />
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing-section" sx={{ py: { xs: 6, md: 10 }, bgcolor: 'grey.100' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={sectionHeadingSx}>
            Simple Pricing, Powerful Features
          </Typography>
          {loadingPlans ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
              {displayPlans.map((plan, idx) => (
                <PricingCard
                  key={plan.id || idx}
                  plan={plan}
                  onSelect={plan.stripePriceId ? handleStartFreeTrial : onLoginClick}
                />
              ))}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4, maxWidth: 700, mx: 'auto' }}>
            Every plan includes the AI assistant with a monthly allowance of{' '}
            {AI_TOKEN_ALLOWANCE.individual.toLocaleString('en-US')} tokens on Individual and{' '}
            {AI_TOKEN_ALLOWANCE.individualMax.toLocaleString('en-US')} tokens on Individual Max. The allowance resets each month.
          </Typography>

          {/* Trust strip */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 4 }}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 5 }}
          >
            {trustPromises.map((promise) => (
              <Link
                key={promise.text}
                href={promise.href}
                target="_blank"
                rel="noopener"
                underline="hover"
                color="text.primary"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
              >
                {promise.icon}
                {promise.text}
              </Link>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={(theme) => ({
          ...brandGradientSx(theme),
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        })}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, mb: 2 }}
          >
            Ready to Up your Reselling Game?
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 4, fontWeight: 300, opacity: 0.95 }}
          >
            Create your account and get 7 days of premium features to explore all the powerful tools Reseller Pro has to offer.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onLoginClick}
            sx={ctaButtonSx}
          >
            Sign Up Now
          </Button>
          <TrialTerms />
        </Container>
      </Box>

      {/* Newsletter Signup */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: '#e8f0fe', textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'heading.main' }}>
            Stay in the Loop
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Subscribe to our newsletter for tips, updates, and reselling insights.
          </Typography>
          {newsletterStatus === 'success' ? (
            <Alert severity="success" sx={{ textAlign: 'left' }}>
              You're subscribed! Thanks for signing up.
            </Alert>
          ) : (
            <form onSubmit={handleNewsletterSubmit}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onChange={(e) => {
                    setNewsletterEmail(e.target.value);
                    if (newsletterStatus === 'error') {
                      setNewsletterStatus('idle');
                      setNewsletterError('');
                    }
                  }}
                  error={newsletterStatus === 'error'}
                  helperText={newsletterStatus === 'error' ? newsletterError : ''}
                  disabled={newsletterSubmitting}
                  aria-label="Email address for newsletter"
                  sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={newsletterSubmitting}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 3 }}
                >
                  {newsletterSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Subscribe'}
                </Button>
              </Stack>
            </form>
          )}
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: 'footer.main',
          color: 'common.white',
          py: 4,
          textAlign: 'center',
        }}
      >
        <Container>
          <Typography variant="body2" sx={{ mb: 1 }}>
            &copy; 2026 Reseller Pro. All rights reserved.
          </Typography>
          <Stack
            direction="row"
            spacing={3}
            justifyContent="center"
            sx={{ fontSize: '0.875rem' }}
          >
            <Link href="/privacy.html" variant="caption" underline="hover" color="inherit" target="_blank" rel="noopener">
              Privacy Policy
            </Link>
            <Link href="/tou.html" variant="caption" underline="hover" color="inherit" target="_blank" rel="noopener">
              Terms of Use
            </Link>
            <Link href="mailto:support@resellerprosoftware.com" variant="caption" underline="hover" color="inherit">
              Contact Us
            </Link>
            <Link
              href="https://www.facebook.com/profile.php?id=61561569696472"
              variant="caption"
              underline="hover"
              color="inherit"
              target="_blank"
              rel="noopener"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <FacebookIcon sx={{ fontSize: 16 }} />
              Follow us on Facebook
            </Link>
            <Link
              href="https://www.youtube.com/@ResellerProSupport"
              variant="caption"
              underline="hover"
              color="inherit"
              target="_blank"
              rel="noopener"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <YouTubeIcon sx={{ fontSize: 16 }} />
              YouTube
            </Link>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
